import {
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	type DialogProps,
	DialogTitle,
	Grid,
	Snackbar,
	TextField,
	makeStyles,
	IconButton,
	FormControl,
	InputLabel,
	OutlinedInput,
} from "@material-ui/core";
import { type SyntheticEvent, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Alert } from "@material-ui/lab";
import { useFormsContext } from "@setup/configurations/forms/context/FormsContext";
import {
	ErrorModal,
	TreePickerControl,
	useAssetContext,
	TimeService,
	getMomentTz,
} from "@dexteel/mesf-core";
import type { Asset } from "@dexteel/mesf-core/dist/models/Asset";
import { TransformAssetCodeModel } from "@/utils/transforms";
import type { FormAsset } from "@setup/configurations/forms/models/FormAsset";
import { upsertFormAsset } from "@setup/configurations/forms/repositories/FormsRepository";
import { ScheduleCodePicker } from "../picker/ScheduleCodePicker";
import { INITIAL_VALUES_FORM_ASSET } from "@setup/configurations/forms/models/FormAssetInitialValue";
import { DateTimePicker } from "@mui/x-date-pickers";
import EditIcon from "@material-ui/icons/Edit";
import cronstrue from "cronstrue";

const moment = getMomentTz();

const useStyles = makeStyles((theme) => ({
	root: {
		"& .input-group-text": {
			display: "none",
		},
		"& .MuiDialogContent-dividers": {
			padding: 0,
		},
	},
	checkbox: {
		userSelect: "none",
	},
	assetPicker: {
		"& .MuiGrid-item": {
			width: "500px",
		},
	},
	modalTitle: {
		backgroundColor: "#ced2cc",
		color: "#495057",
		textAlign: "center",
	},
	modalWarning: {
		color: "white",
		backgroundColor: "#dc3545",
		textAlign: "center",
	},
	messageTitle: {
		fontSize: 15,
		fontWeight: "bold",
		color: "#0000008A",
	},
	inputEditable: {
		"& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
			border: "2px solid #3F51B5",
		},
	},
	comments: {
		"& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
			border: "1px solid #3F51B5",
		},
	},
	cronField: {
		"& .MuiInputBase-root": {
			height: "40px",
		},
		"& .MuiOutlinedInput-input": {
			padding: "10px 14px",
		},
	},
	scheduleFormControl: {
		width: "100%",
		"& .MuiInputBase-root": {
			height: "40px",
		},
		"& .MuiOutlinedInput-input": {
			padding: "10px 14px",
		},
		"& .MuiInputLabel-outlined": {
			transform: "translate(14px, 12px) scale(1)",
		},
		"& .MuiInputLabel-shrink": {
			transform: "translate(14px, -6px) scale(0.75)",
		},
	},
	disabledDatePicker: {
		"& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline": {
			borderWidth: "1px !important",
			borderColor: "rgba(0, 0, 0, 0.23)",
		},
		"& .MuiInputBase-input.Mui-disabled": {
			color: "rgba(0, 0, 0, 0.38)",
		},
		"& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
			borderWidth: "1px",
		},
	},
	errorLabel: {
		fontSize: 12,
		color: "#F44336",
	},
	formRow: {
		display: "flex",
		flexWrap: "wrap",
		marginTop: 10,
		width: "100%",
	},
	dialogContentFixed: {
		maxHeight: "60vh",
		overflowY: "auto",
		"@media (max-height: 600px)": {
			maxHeight: "50vh",
		},
	},
}));

type Props = {
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
};

export const NewFormAssetModal = ({ show, onHide }: Props) => {
	const classes = useStyles();
	const [addSuccess, setAddSuccess] = useState(false);
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [error, setError] = useState<string>("");
	const {
		state: {
			assetCodes,
			FormName,
			FormId,
			userFormAssetData: { AssetId, AssetName },
		},
		actions: {
			setAssetCodes,
			setAssetId,
			setAssetName,
			setFormAssetFormAssetId,
			setFormAssetAssetId,
		},
	} = useFormsContext();

	const {
		state: { allAssets },
	} = useAssetContext();

	const [fullWidth, setFullWidth] = useState(true);
	const [maxWidth, setMaxWidth] = useState<DialogProps["maxWidth"]>("sm");
	const [cronExpression, setCronExpression] = useState<string>("");
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isCustomSchedule, setIsCustomSchedule] = useState(false);

	const {
		control,
		handleSubmit,
		setValue,
		watch,
		formState: { isValid, errors },
		reset,
	} = useForm<FormAsset>({
		defaultValues: INITIAL_VALUES_FORM_ASSET,
	});

	const schedulePolicyCode = watch("SchedulePolicyCode");

	const handleCancel = async () => {
		reset();
		setAssetId(1);
		setAssetName("Plant");
		onHide(false);
	};

	const buildTreeAssets = (nodes: any) => {
		if (nodes === undefined) return;
		const idMapping = nodes.reduce((acc: any, el: any, i: number) => {
			acc[el.id] = i;
			return acc;
		}, {});
		let root;
		nodes.forEach((el: any) => {
			if (el.parentId === null) {
				root = el;
				return;
			}
			const parentEl = nodes[idMapping[el.parentId]];
			if (parentEl) {
				parentEl.children = [...(parentEl?.children || []), el];
			}
		});
		setAssetCodes(root as any);
	};

	const loadFilterAssets = () => {
		let mydataSource: any[] = [];
		allAssets.forEach((row: Asset) => {
			const code = TransformAssetCodeModel(row);
			mydataSource = [...mydataSource, code];
		});
		buildTreeAssets(mydataSource);
	};

	const changeSearchAsset = (value: any, description: any) => {
		setValue("AssetId", Number.parseInt(value));
		setAssetId(Number.parseInt(value));
		setFormAssetAssetId(Number.parseInt(value));
		setAssetName(description);
	};

	const handleClose = (event?: SyntheticEvent, reason?: string) => {
		if (reason === "clickaway") {
			return;
		}
		setAddSuccess(false);
	};

	const onSubmit = async (data: FormAsset) => {
		setIsSubmitLoading(true);

		const submitData = {
			...data,
			CronExpression: cronExpression,
		};

		const res = await upsertFormAsset(submitData);
		if (res.ok) {
			setFormAssetFormAssetId(null);
			setIsSubmitLoading(false);
			setAddSuccess(true);
			reset();
			onHide(true);
		} else {
			setIsSubmitLoading(false);
			setError(res.message);
		}
	};

	const handleScheduleStartChange = (date: Date) => {
		setValue("StartDate", date);
	};

	useEffect(() => {
		if (show) {
			reset();
			setAssetId(1);
			setAssetName("Plant");
			setValue("AssetId", AssetId);
			setValue("FormId", FormId);
			setValue("StartDate", new Date());
			setIsSubmitLoading(false);
			loadFilterAssets();
			setCronExpression("");
			setIsCustomSchedule(false);
		}
	}, [show]);

	useEffect(() => {
		setIsCustomSchedule(schedulePolicyCode === "P");
	}, [schedulePolicyCode]);

	return (
		<>
			<Dialog
				fullWidth={fullWidth}
				maxWidth={maxWidth}
				open={show}
				onClose={() => handleCancel()}
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<Grid container style={{ padding: "15px 0 20px" }}>
						<DialogTitle
							style={{
								padding: "5px 40px 10px 24px",
								marginRight: 20,
								width: "100%",
							}}
						>
							<Grid item md={12} xs={12}>
								<h1 style={{ fontSize: "1em" }}>FORM: {FormName}</h1>
							</Grid>
						</DialogTitle>
						<Grid container item>
							<DialogContent
								dividers
								style={{ width: "100%", padding: "20px" }}
								className={classes.dialogContentFixed}
							>
								<Grid container>
									<Grid
										item
										md={12}
										xs={12}
										style={{ paddingLeft: 0, marginBottom: 15 }}
										className={classes.inputEditable}
									>
										<Controller
											name="AssetId"
											control={control}
											render={() => (
												<TreePickerControl
													className={classes.assetPicker}
													inputTitle="Asset"
													title="Choose Asset"
													dataSource={assetCodes}
													value={AssetId}
													description={AssetName}
													onSelect={changeSearchAsset}
													selectBranch={true}
													selectActive={false}
													selectInternal={false}
													allowSearch={true}
												></TreePickerControl>
											)}
										/>
									</Grid>

									<div className={classes.formRow}>
										<Grid
											item
											md={4}
											xs={12}
											style={{ paddingRight: 10 }}
											className={classes.inputEditable}
										>
											<Controller
												name="SchedulePolicyCode"
												control={control}
												render={({ field: { onChange, value } }) => (
													<ScheduleCodePicker
														value={value as string}
														onChange={onChange}
														cronExpression={cronExpression}
														onCronExpressionChange={setCronExpression}
														isModalOpen={isModalOpen}
														setIsModalOpen={setIsModalOpen}
														hideScheduleStart={setIsCustomSchedule}
														onScheduleStartChange={handleScheduleStartChange}
													/>
												)}
											/>
										</Grid>

										<Grid
											item
											md={2}
											xs={12}
											style={{ paddingRight: 10 }}
											className={classes.inputEditable}
										>
											<Controller
												name="Periods"
												control={control}
												rules={{ required: true, pattern: /^[0-9]*$/ }}
												render={({
													field: { onChange, value },
													fieldState: { error },
												}) => (
													<TextField
														className={classes.root}
														style={{ margin: "8px 0" }}
														size="small"
														label="Periods"
														multiline
														variant="outlined"
														fullWidth
														autoComplete="off"
														value={value}
														onChange={(evt) => {
															const newValue = evt.target.value.replace(
																/[^0-9]/g,
																"",
															);
															if (newValue.length <= 99) onChange(newValue);
														}}
														error={!!error}
														helperText={error ? "Enter a period number" : ""}
													/>
												)}
											/>
										</Grid>

										<Grid item md={6} xs={12} className={classes.inputEditable}>
											<Controller
												name="StartDate"
												control={control}
												render={({ field }) => (
													<DateTimePicker
														label="Start Date"
														timezone={
															isCustomSchedule
																? "UTC"
																: TimeService.getInstance().getServerTimeZone()
														}
														format={
															isCustomSchedule
																? "MM/DD/YYYY"
																: "MM/DD/YYYY HH:mm"
														}
														timeSteps={{ minutes: 1 }}
														value={moment(field.value) as any}
														onChange={field.onChange}
														inputRef={field.ref}
														disabled={isCustomSchedule}
														sx={{
															width: "100%",
															"& .MuiFormLabel-root": {
																marginTop: "-4px !important",
																color: "#858585",
															},
															"& .MuiButtonBase-root": {
																marginTop: "8px !important",
															},
														}}
														slotProps={{
															textField: {
																fullWidth: true,
																variant: "outlined",
																error: !!errors.StartDate,
																style: {
																	margin: "8px 0",
																},
																InputProps: {
																	style: {
																		height: "40px",
																	},
																},
																className: classes.disabledDatePicker,
															},
														}}
													/>
												)}
											/>
										</Grid>
									</div>

									{isCustomSchedule && (
										<Grid
											item
											md={12}
											xs={12}
											className={classes.inputEditable}
											style={{ marginTop: "8px" }}
										>
											<FormControl
												variant="outlined"
												margin="dense"
												className={classes.scheduleFormControl}
											>
												<InputLabel>Schedule</InputLabel>
												<OutlinedInput
													placeholder="Choose your schedule"
													value={
														cronExpression
															? cronstrue.toString(cronExpression)
															: ""
													}
													readOnly={true}
													label="Schedule"
													endAdornment={
														<IconButton
															edge="end"
															onClick={() => setIsModalOpen(true)}
															size="small"
														>
															<EditIcon />
														</IconButton>
													}
												/>
											</FormControl>
										</Grid>
									)}
								</Grid>
							</DialogContent>
							<Grid
								item
								md={12}
								xs={12}
								justifyContent="flex-end"
								style={{ marginTop: "15px" }}
							>
								<DialogActions style={{ padding: "0px 24px" }}>
									<Grid
										container
										justifyContent="flex-end"
										spacing={2}
										style={{ margin: 0 }}
									>
										<Grid item md={3} xs={4}>
											<Button
												fullWidth
												variant="contained"
												color="default"
												onClick={handleCancel}
											>
												Cancel
											</Button>
										</Grid>
										<Grid item md={3} xs={4} style={{ paddingRight: 0 }}>
											<Button
												fullWidth
												startIcon={
													isSubmitLoading && <CircularProgress size="1rem" />
												}
												disabled={
													!isValid ||
													isSubmitLoading ||
													(schedulePolicyCode === "P" &&
														(!cronExpression ||
															cronExpression.trim().length === 0))
												}
												variant="contained"
												color="primary"
												type="submit"
											>
												Save
											</Button>
										</Grid>
									</Grid>
								</DialogActions>
							</Grid>
						</Grid>
					</Grid>
				</form>
			</Dialog>
			<Snackbar open={addSuccess} autoHideDuration={2500} onClose={handleClose}>
				<Alert severity="info" onClose={handleClose}>
					Success
				</Alert>
			</Snackbar>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
